import { ChangeEvent, useCallback, MouseEvent } from 'react';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormStateReturn,
  UseFormWatch,
} from 'react-hook-form';
import { formatWithSymbolAndCode } from '@foxtrotplatform/developer-platform-common-ui-component-lib';

import { AddonPlanItem, FormFamily, SelectedAddon } from '../types';
import { isAnotherFamiliesProductSelected } from './planUtility';

interface AddOnPlanCardProps<T extends FieldValues> {
  item: AddonPlanItem;
  control?: Control<T, any>;
  inputKey?: Path<T>;
  selectedSolutionLabel?: string;
  watch: UseFormWatch<T>;
  openCleanSolutionModal: () => void;
}

export const AddonPlanCard = <T extends FieldValues>({
  item,
  control,
  inputKey,
  selectedSolutionLabel,
  watch,
  openCleanSolutionModal,
}: AddOnPlanCardProps<T>) => {
  const { displayName, shortDescription, tiers } = item;
  const addonTiers = tiers.map(({ tierId, label, pricing }) => {
    return {
      label,
      id: tierId,
      cost: pricing.originalPriceAfterDiscount,
    };
  });
  const families: FormFamily[] = watch('families' as Path<T>);

  const handleOnChange = useCallback(
    (formCallback: (value: any) => void) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        if (isAnotherFamiliesProductSelected(families)) {
          openCleanSolutionModal();
        } else if (e.currentTarget.checked) {
          const newItem = {
            ...item,
            selectedTier: selectedSolutionLabel || 'unselected-product',
          };
          formCallback({ item: { ...newItem } });
        } else {
          const newItem = { ...item, selectedTier: undefined };
          formCallback({ item: { ...newItem } });
        }
      },
    [item, selectedSolutionLabel, families, openCleanSolutionModal]
  );

  const handleOnClick = useCallback(
    (formCallback: (value: any) => void, value: SelectedAddon) =>
      (e: MouseEvent<HTMLButtonElement>) => {
        if (isAnotherFamiliesProductSelected(families)) {
          openCleanSolutionModal();
        } else if (!value?.item?.selectedTier) {
          const newItem = {
            ...item,
            selectedTier: selectedSolutionLabel || 'unselected-product',
          };
          formCallback({ item: { ...newItem } });
        } else {
          const newItem = { ...item, selectedTier: undefined };
          formCallback({ item: { ...newItem } });
        }
      },
    [item, selectedSolutionLabel, families, openCleanSolutionModal]
  );

  const renderAddonTierInformation = useCallback(() => {
    if (selectedSolutionLabel) {
      const matchingAddonTier = addonTiers.find(
        ({ label }) => label === selectedSolutionLabel
      );
      if (matchingAddonTier) {
        return (
          <div className="flex flex-col items-left text-base font-bold text-secondary-800">
            <div>{matchingAddonTier.label}</div>
            {matchingAddonTier && matchingAddonTier?.cost ? (
              <div>
                {formatWithSymbolAndCode(
                  matchingAddonTier?.cost?.amount,
                  matchingAddonTier?.cost?.currency,
                  matchingAddonTier?.cost?.precision
                )}
                /mo
              </div>
            ) : null}
          </div>
        );
      }
    }
    return addonTiers.map(({ cost, label }, i) => {
      return (
        <div
          className="flex flex-col items-left text-base font-bold text-secondary-800"
          key={i}
        >
          <div>{label}</div>
          {cost ? (
            <div>
              {formatWithSymbolAndCode(
                cost.amount,
                cost.currency,
                cost.precision
              )}
              /mo
            </div>
          ) : null}
        </div>
      );
    });
  }, [selectedSolutionLabel, addonTiers]);

  const renderInputCheckbox = useCallback(
    ({
      field: { value, onChange, ref },
    }: {
      field: ControllerRenderProps<T, Path<T>>;
      fieldState: ControllerFieldState;
      formState: UseFormStateReturn<T>;
    }) => {
      return (
        <button
          type="button"
          onClick={handleOnClick(onChange, value)}
          className="flex gap-3 bg-white rounded-md border border-danger-100 gap-y-3 py-4 pr-5 ml-8"
        >
          <div className="pl-3 pt-1">
            <input
              type="checkbox"
              checked={!!value?.item?.selectedTier}
              onChange={handleOnChange(onChange)}
              className="h-4.5 w-4.5 border-slate-400 rounded-sm shadow mb-3"
              ref={ref}
            />
          </div>
          <div className="grow flex flex-col sm:flex-row gap-2 justify-between">
            <div className="grow flex flex-col gap-y-2">
              <div className="flex flex-row items-center justify-between">
                <span className="text-base font-bold text-slate-700">
                  {displayName}
                </span>{' '}
              </div>
              <div className="flex flex-col text-sm text-black text-left">
                <span>{shortDescription}</span>
              </div>
            </div>
            <div className="flex flex-row gap-x-4">
              {renderAddonTierInformation()}
            </div>
          </div>
        </button>
      );
    },
    [
      handleOnClick,
      renderAddonTierInformation,
      handleOnChange,
      displayName,
      shortDescription,
    ]
  );

  if (!control || !inputKey) {
    return null;
  }

  return (
    <Controller
      name={inputKey}
      control={control}
      render={renderInputCheckbox}
    />
  );
};
